import { IOrder } from '../model/IOrder';
import { IOrderCategorySummary } from '../model/IOrderSummary';
import { IPageParams } from './IApi';
import { IPagesResponse } from './IPagesResponse';
import { buildOrderSearchParams, IOrderFilterState } from './orderFilter';
import api from './api';

export const orderApi = api.injectEndpoints({
  endpoints: (build) => ({
    // --
    // -- OrderManagementController#searchOrder. Filter fields map to
    // -- OrderFilter; Pageable takes page/size/sort as `field,dir`.
    // --
    // -- A vendor's results are narrowed to their own supplier keys by the
    // -- backend, from the signed-in principal - never by anything sent here.
    // --
    getOrderList: build.query<IPagesResponse<IOrder[]>, IPageParams & IOrderFilterState>({
      query: (args) => ({
        url: 'ordermanagement/search',
        params: buildOrderSearchParams(args),
      }),
      providesTags: ['Orders'],
    }),

    getOrder: build.query<IOrder, string>({
      query: (orderId) => ({
        url: `ordermanagement/order/${orderId}`,
      }),
      providesTags: ['Order'],
    }),

    // --
    // -- Orders grouped by the day they were created. Not paged - it is one
    // -- row per day and only reads as a whole.
    // --
    getOrdersByDate: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_date',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by vendor, over the same columns.
    getOrdersByVendor: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_vendor',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the vehicle their items are for.
    getOrdersByVehicle: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_vehicle',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by part type - the item's name.
    getOrdersByPartType: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_part_type',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by status, over the by-date columns.
    getOrdersByStatus: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_status',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by how they were paid for, by stored payment code.
    getOrdersByPaymentType: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_payment_type',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by DMA - the market area the shipping zip sits in.
    getOrdersByDma: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_dma',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the state they shipped to.
    getOrdersByState: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_state',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the site they came from.
    getOrdersByWebsite: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_website',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),
  }),
});
